import ApartmentIcon from '@mui/icons-material/Apartment';
import { HealingOutlined } from '@mui/icons-material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonIcon from '@mui/icons-material/Person';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import {
  addAdmission,
  resetCurrentAdmission,
} from '../../redux/admissions/admissionsActions';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import {
  fetchPatient,
} from '../../redux/patient/patientAction';
import { fetchWards } from '../../redux/ward/wardAction';
import { formatToOptions } from './admissionUtils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getCurrentDate = () => new Date().toISOString().split('T')[0];

const clearLocationStorage = () => {
  localStorage.removeItem('block');
  localStorage.removeItem('floor');
  localStorage.removeItem('ward');
};

// ---------------------------------------------------------------------------
// Custom hook
// ---------------------------------------------------------------------------
const useAdmissionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: '',
    message: '',
    type: 'success',
  });

  // Persist block/floor/ward selections across page loads
  const storedBlock = localStorage.getItem('block') || '';
  const storedFloor = localStorage.getItem('floor') || '';
  const storedWard  = localStorage.getItem('ward')  || '';


  // ---- form setup --------------------------------------------------------
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patient_id: '',
      block_id: storedBlock,
      floor_id: storedFloor,
      ward_id: storedWard,
      department_id: '',
      attender_type: '1',
      no_of_attenders: '1',
      attender_name: '',
      attender_relation: '',
      attender_address: '',
      attender_phone: '',
      attender_name_2: '',
      attender_relation_2: '',
      attender_address_2: '',
      attender_phone_2: '',
      admit_date: getCurrentDate(),
    },
  });

  // ---- selectors ---------------------------------------------------------
  const blockList        = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList        = useSelector((state) => state?.floor?.fetchFloors || {});
  const departmentList   = useSelector((state) => state?.department?.fetchDepartments || {});
  const wardList         = useSelector((state) => state?.ward?.fetchWards || {});
  const patientList      = useSelector((state) => state?.patients?.fetchPatient || {});
  const admissionState   = useSelector((state) => state?.admission?.addAdmission || {});
  console.log("patientList", patientList);
  const isBlockLoading      = useSelector((state) => state?.block?.fetchBlocks?.inProgress || false);
  const isFloorLoading      = useSelector((state) => state?.floor?.fetchFloors?.inProgress || false);
  const isWardLoading       = useSelector((state) => state?.ward?.fetchWards?.inProgress || false);
  const isDepartmentLoading = useSelector((state) => state?.department?.fetchDepartments?.inProgress || false);
  const isPatientLoading    = useSelector((state) => state?.patients?.fetchPatient?.inProgress || false);
  const user             = useSelector((state) => state?.values?.patientData || {});
  const patientId = user.id;

  // ---- watched values ----------------------------------------------------
  const watchPatientId     = watch('patient_id');
  const watchAttenderType  = watch('attender_type');
  const watchNoOfAttenders = watch('no_of_attenders');
  const watchBlock         = watch('block_id');
  const watchFloor         = watch('floor_id');
  const watchWard          = watch('ward_id');

  // ---- initial data fetch ------------------------------------------------
  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId));
    }
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
  }, [dispatch, patientId]);




  // ---- set patient_id + today's date from redux user ---------------------
  useEffect(() => {
    setValue('patient_id', patientId || '');
    setValue('admit_date', getCurrentDate());
  }, [patientId, setValue]);

  // ---- pre-fill attender address / phone from patientList ----------------
  useEffect(() => {
    if (watchPatientId && patientList?.data?.data) {
      const selectedPatient = patientList.data.data;
      if (selectedPatient && selectedPatient.id === watchPatientId) {
        setValue('attender_address', selectedPatient.address || '');
        setValue('attender_phone', selectedPatient.phone || '');
      }
    }
  }, [watchPatientId, patientList, setValue]);



  // ---- block → fetch floors, reset dependents ----------------------------
  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchFloors(watchBlock));
      if (watchBlock !== storedBlock) {
        setValue('floor_id', '');
        setValue('ward_id', '');
      }
    }
  }, [watchBlock, dispatch, setValue, storedBlock]);

  // ---- block + floor → fetch wards, reset ward ---------------------------
  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor));
      if (watchFloor !== storedFloor) setValue('ward_id', '');
    }
  }, [watchBlock, watchFloor, dispatch, setValue, storedFloor]);

  // ---- persist location selections to localStorage -----------------------
  useEffect(() => {
    if (watchBlock) localStorage.setItem('block', watchBlock);
    if (watchFloor) localStorage.setItem('floor', watchFloor);
    if (watchWard)  localStorage.setItem('ward',  watchWard);
  }, [watchBlock, watchFloor, watchWard]);

  // ---- handle admission success / error ----------------------------------
  useEffect(() => {
    if (admissionState.success) {
      if (watchAttenderType === '1') {
        navigate(`/admission/view/${watchPatientId}`);
      } else {
        navigate(APP_LINK.LISTADMISSIONDETAILS);
      }
      dispatch(resetCurrentAdmission());
    } else if (admissionState.error) {
      setDialogProps({
        title: 'Error',
        message: 'Failed to submit Admission request. Please try again.',
        type: 'error',
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    }
  }, [admissionState, dispatch, navigate, watchAttenderType, watchPatientId]);

  // ---- handlers ----------------------------------------------------------
  const handleCancel = () => {
    reset();
    clearLocationStorage();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogProps.type === 'success') {
      reset();
      clearLocationStorage();
    }
    navigate(APP_LINK.LISTADMISSIONDETAILS);
  };

  const onSubmit = (data) => {
    if (!data.block_id || !data.floor_id || !data.ward_id) {
      setDialogProps({
        title: 'Error',
        message: 'Please select Block, Floor and Ward',
        type: 'error',
      });
      setDialogOpen(true);
      return;
    }

    const formattedDate = data.admit_date
      ? `${data.admit_date} 00:00:00`
      : `${getCurrentDate()} 00:00:00`;

    const formdata = {
      patient_id:    data.patient_id,
      block_id:      data.block_id,
      floor_id:      data.floor_id,
      ward_id:       data.ward_id,
      department_id: data.department_id || '',
      admit_date:    formattedDate,
      attender_type: data.attender_type,
    };

    if (data.attender_type === '1') {
      const attenderDetails = [
        {
          attender_name: data.attender_name,
          relation:      data.attender_relation,
          address:       data.attender_address,
          phone:         data.attender_phone,
        },
      ];
      if (data.no_of_attenders === '2') {
        attenderDetails.push({
          attender_name: data.attender_name_2,
          relation:      data.attender_relation_2,
          address:       data.attender_address_2,
          phone:         data.attender_phone_2,
        });
      }
      formdata.number_attenders = data.no_of_attenders;
      formdata.attender_details = attenderDetails;
    } else {
      formdata.number_attenders = '0';
      formdata.attender_details = [];
    }

    dispatch(addAdmission(formdata));
  };


  // ---- memoised field option builders ------------------------------------
  const patientOptions = useMemo(() => {
    const uniquePatients = [];
    const seenLabels = new Set();
    const dataList = patientList?.data?.data ? [patientList.data.data] : [];

    dataList.forEach((patient) => {
      const label = `${patient.patient_name} - ${patient.phone} - ${patient.ip_number}`;
      if (!seenLabels.has(label)) {
        seenLabels.add(label);
        uniquePatients.push({ value: patient.id, label });
      }
    });
    return uniquePatients;
  }, [patientList?.data?.data]);

  const blockOptions = useMemo(
    () => formatToOptions(blockList?.Blocks?.data, "block_name", "id", (b) => b.block_status !== 2),
    [blockList?.Blocks?.data]
  );

  const floorOptions = useMemo(
    () => formatToOptions(floorList?.floors?.data, "floor_name", "id", (f) => f.floor_status !== 0),
    [floorList?.floors?.data]
  );

  const wardOptions = useMemo(
    () => formatToOptions(wardList?.wards?.data, "ward_name", "id", (w) => w.status !== 0),
    [wardList?.wards?.data]
  );

  const departmentOptions = useMemo(
    () => formatToOptions(departmentList?.departments?.data, "department_name", "id", (d) => d.status !== 2),
    [departmentList?.departments?.data]
  );

  // ---- memoised field arrays with dynamic options -----------------------
  const patientFields = useMemo(
    () => [
      {
        name: 'patient_id',
        label: 'Patient Name / நோயாளியை தேர்ந்தெடுக்கவும்',
        type: 'select',
        options: patientOptions,
        rules: { required: 'Patient selection is required' },
        startIcon: <PersonIcon />,
        loading: isPatientLoading,
        loadingText: "Loading patients... / நோயாளிகள் ஏற்றப்படுகிறார்கள்...",
        noOptionsText: "No Patients found / நோயாளிகள் இல்லை",
        disabled: true,
      },
      {
        name: 'admit_date',
        label: 'Admission Date / சேர்க்கை தேதி',
        type: 'date',
        tabIndex: 1,
        rules: { required: 'Admit Date is required' },
        startIcon: <PersonIcon />,
      },
    ],
    [patientOptions, isPatientLoading]
  );

  const locationFields = useMemo(
    () => [
      {
        name: 'block_id',
        label: 'Block / கட்டிடம்',
        placeholder: 'Select block',
        type: 'select',
        tabIndex: 2,
        options: blockOptions,
        rules: { required: 'Block is required' },
        startIcon: <LocalHospitalIcon />,
        loading: isBlockLoading,
        loadingText: "Loading blocks... / கட்டிடங்கள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No blocks found / கட்டிடங்கள் இல்லை",
      },
      {
        name: 'ward_id',
        label: 'Ward Name / வார்டு பெயர்',
        placeholder: 'Select ward name',
        type: 'select',
        tabIndex: 4,
        options: wardOptions,
        rules: { required: 'Ward name is required' },
        startIcon: <MeetingRoomIcon />,
        loading: isWardLoading,
        loadingText: "Loading wards... / வார்டுகள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No wards found / வார்டுகள் இல்லை",
      },
    ],
    [blockOptions, wardOptions, isBlockLoading, isWardLoading]
  );

  const wardDeptFields = useMemo(
    () => [
      {
        name: 'floor_id',
        label: 'Floor / மாடி',
        placeholder: 'Select Floor',
        type: 'select',
        tabIndex: 3,
        options: floorOptions,
        rules: { required: 'Floor is required' },
        startIcon: <ApartmentIcon />,
        loading: isFloorLoading,
        loadingText: "Loading floors... / மாடிகள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No floors found / மாடிகள் இல்லை",
      },
      {
        name: 'department_id',
        label: 'Department / துறை',
        placeholder: 'Select department',
        type: 'select',
        tabIndex: 5,
        options: departmentOptions,
        rules: { required: 'Department is required' },
        startIcon: <HealingOutlined />,
        loading: isDepartmentLoading,
        loadingText: "Loading departments... / துறைகள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No departments found / துறைகள் இல்லை",
      },
    ],
    [floorOptions, departmentOptions, isFloorLoading, isDepartmentLoading]
  );

  return {
    // form controls
    register,
    handleSubmit,
    setValue,
    control,
    errors,
    // watched values
    watchAttenderType,
    watchNoOfAttenders,
    // submission / state
    admissionState,
    onSubmit,
    handleCancel,
    // dialog
    dialogOpen,
    dialogProps,
    handleCloseDialog,
    // field arrays
    patientFields,
    locationFields,
    wardDeptFields,
  };
};

export default useAdmissionForm;
